import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers, Shield, Plus, Edit2, Trash2, Check, X,
  Store, Users, Package, DollarSign, Loader, Zap, Star, Crown
} from 'lucide-react';
import './PlansManagement.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'XOF', minimumFractionDigits: 0
}).format(n || 0);

const DEFAULT_PLANS = [
  {
    id: 'starter',
    nom: 'Starter',
    prix: 100000,
    description: 'Parfait pour démarrer',
    couleur: '#16a34a',
    bg: '#ecfdf5',
    icon: 'Zap',
    limites: { boutiques: 1, utilisateurs: 2, produits: null },
  },
  {
    id: 'pro',
    nom: 'Pro',
    prix: 150000,
    description: 'Pour les équipes en croissance',
    couleur: '#2563eb',
    bg: '#eff6ff',
    icon: 'Star',
    limites: { boutiques: 3, utilisateurs: 5, produits: null },
  },
  {
    id: 'entreprise',
    nom: 'Entreprise',
    prix: 300000,
    description: 'Solution complète sans limites',
    couleur: '#9333ea',
    bg: '#fdf4ff',
    icon: 'Crown',
    limites: { boutiques: null, utilisateurs: null, produits: null },
  },
];

const PLAN_ICONS = { Zap, Star, Crown };

const EMPTY_FORM = {
  id: '', nom: '', prix: 0, description: '', couleur: '#6366f1', bg: '#eef2ff', icon: 'Zap',
  limites: { boutiques: null, utilisateurs: null, produits: null },
};

export default function PlansManagement() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({ ...plan });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      if (editing) {
        setPlans(plans.map(p => p.id === editing.id ? form : p));
      } else {
        setPlans([...plans, form]);
      }
      closeModal();
      setSubmitting(false);
    }, 300);
  };

  const handleDelete = (planId) => {
    if (!window.confirm('Supprimer ce plan ?')) return;
    setPlans(plans.filter(p => p.id !== planId));
  };

  const setLim = (field, val) =>
    setForm(f => ({ ...f, limites: { ...f.limites, [field]: val ? parseInt(val) : null } }));

  const totalCA = plans.reduce((s, p) => s + p.prix, 0);

  return (
    <div className="pm-page">

      {/* ── Header ── */}
      <div className="pm-header">
        <div className="pm-header-left">
          <div className="pm-header-icon"><Layers size={20}/></div>
          <div>
            <h1 className="pm-header-title">Gestion des Plans</h1>
            <p className="pm-header-sub">Configuration des offres d'abonnement SaaS</p>
          </div>
        </div>
        <div className="pm-header-right">
          <button className="pm-nav-btn" onClick={() => navigate('/super-admin')}>
            <Shield size={13}/> Dashboard
          </button>
          <button className="pm-nav-btn" onClick={() => navigate('/saas-management')}>
            <Store size={13}/> Abonnements
          </button>
          <button className="pm-cta-btn" onClick={openCreate}>
            <Plus size={14}/> Nouveau plan
          </button>
        </div>
      </div>

      <div className="pm-content">

        {/* KPI strip */}
        <div className="pm-kpi-row">
          <div className="pm-kpi">
            <div className="pm-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
              <Layers size={16}/>
            </div>
            <div className="pm-kpi-val">{plans.length}</div>
            <div className="pm-kpi-label">Plans disponibles</div>
          </div>
          <div className="pm-kpi">
            <div className="pm-kpi-icon" style={{ background: '#ecfdf5', color: '#16a34a' }}>
              <DollarSign size={16}/>
            </div>
            <div className="pm-kpi-val">{fmt(totalCA)}</div>
            <div className="pm-kpi-label">Revenu potentiel (si 1 client/plan)</div>
          </div>
          <div className="pm-kpi">
            <div className="pm-kpi-icon" style={{ background: '#fff8ec', color: '#f59e0b' }}>
              <Users size={16}/>
            </div>
            <div className="pm-kpi-val">{plans.reduce((s,p) => s + (p.limites.utilisateurs || 0), 0) || '∞'}</div>
            <div className="pm-kpi-label">Utilisateurs max (tous plans)</div>
          </div>
        </div>

        {/* Plans grid */}
        <div className="pm-plans-grid">
          {plans.map((plan) => {
            const Icon = PLAN_ICONS[plan.icon] || Layers;
            return (
              <div key={plan.id} className="pm-plan-card" style={{ borderTopColor: plan.couleur }}>
                <div className="pm-plan-card-top" style={{ background: plan.bg }}>
                  <div className="pm-plan-icon" style={{ background: plan.couleur + '22', color: plan.couleur }}>
                    <Icon size={20}/>
                  </div>
                  <div>
                    <h3 className="pm-plan-name" style={{ color: plan.couleur }}>{plan.nom}</h3>
                    <p className="pm-plan-desc">{plan.description}</p>
                  </div>
                  <span className="pm-plan-id-chip">{plan.id}</span>
                </div>

                <div className="pm-plan-card-body">
                  <div className="pm-plan-price">
                    <span className="pm-plan-price-amount">{fmt(plan.prix)}</span>
                    <span className="pm-plan-price-period">/ mois</span>
                  </div>

                  <div className="pm-plan-features">
                    {[
                      { icon: Store,   label: 'Boutiques',    val: plan.limites.boutiques },
                      { icon: Users,   label: 'Utilisateurs', val: plan.limites.utilisateurs },
                      { icon: Package, label: 'Produits',     val: plan.limites.produits },
                    ].map(({ icon: FIcon, label, val }) => (
                      <div key={label} className="pm-feature">
                        <FIcon size={13} style={{ color: plan.couleur }}/>
                        <span className="pm-feature-label">{label}</span>
                        <span className="pm-feature-val" style={{ color: val ? '#0f172a' : '#16a34a' }}>
                          {val ? val : '∞ Illimité'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pm-plan-card-footer">
                  <button className="pm-btn-edit" onClick={() => openEdit(plan)}>
                    <Edit2 size={12}/> Modifier
                  </button>
                  <button className="pm-btn-delete" onClick={() => handleDelete(plan.id)}>
                    <Trash2 size={12}/> Supprimer
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add placeholder */}
          <button className="pm-plan-add-card" onClick={openCreate}>
            <div className="pm-plan-add-icon"><Plus size={22}/></div>
            <div className="pm-plan-add-label">Ajouter un plan</div>
          </button>
        </div>

      </div>

      {/* ── Modal ── */}
      {showModal && (
        <>
          <div className="pm-overlay" onClick={closeModal}/>
          <div className="pm-modal">
            <div className="pm-modal-head">
              <div>
                <h2 className="pm-modal-title">
                  {editing ? 'Modifier le plan' : 'Nouveau plan'}
                </h2>
                <p className="pm-modal-sub">
                  {editing ? `Mise à jour du plan ${editing.nom}` : 'Configurer un nouveau plan d\'abonnement'}
                </p>
              </div>
              <button className="pm-modal-close" onClick={closeModal}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit} className="pm-modal-body">

              <div className="pm-form-row">
                <div className="pm-form-group">
                  <label>ID du plan *</label>
                  <input value={form.id}
                    onChange={e => setForm({...form, id: e.target.value})}
                    placeholder="starter, pro, entreprise…"
                    disabled={!!editing} required/>
                  <p className="pm-form-hint">Identifiant unique · minuscules sans espaces</p>
                </div>
                <div className="pm-form-group">
                  <label>Nom affiché *</label>
                  <input value={form.nom}
                    onChange={e => setForm({...form, nom: e.target.value})}
                    placeholder="Ex: Starter" required/>
                </div>
              </div>

              <div className="pm-form-group">
                <label>Description</label>
                <input value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="Décrivez ce plan en une ligne"/>
              </div>

              <div className="pm-form-row">
                <div className="pm-form-group">
                  <label>Prix mensuel (XOF) *</label>
                  <input type="number" min="0" step="1000"
                    value={form.prix}
                    onChange={e => setForm({...form, prix: parseInt(e.target.value) || 0})}
                    required/>
                </div>
                <div className="pm-form-group">
                  <label>Icône</label>
                  <select value={form.icon} onChange={e => setForm({...form, icon: e.target.value})}>
                    <option value="Zap">⚡ Zap (Starter)</option>
                    <option value="Star">⭐ Star (Pro)</option>
                    <option value="Crown">👑 Crown (Entreprise)</option>
                  </select>
                </div>
              </div>

              <div className="pm-form-row">
                <div className="pm-form-group">
                  <label>Couleur principale</label>
                  <div className="pm-color-wrap">
                    <input type="color" value={form.couleur}
                      onChange={e => setForm({...form, couleur: e.target.value})}/>
                    <span>{form.couleur}</span>
                  </div>
                </div>
                <div className="pm-form-group">
                  <label>Couleur fond</label>
                  <div className="pm-color-wrap">
                    <input type="color" value={form.bg}
                      onChange={e => setForm({...form, bg: e.target.value})}/>
                    <span>{form.bg}</span>
                  </div>
                </div>
              </div>

              <div className="pm-section-title">Limites du plan</div>
              <p className="pm-section-sub">Laissez vide pour illimité</p>

              <div className="pm-form-row pm-form-row--3">
                <div className="pm-form-group">
                  <label><Store size={12}/> Boutiques</label>
                  <input type="number" min="0" placeholder="Illimité"
                    value={form.limites.boutiques || ''}
                    onChange={e => setLim('boutiques', e.target.value)}/>
                </div>
                <div className="pm-form-group">
                  <label><Users size={12}/> Utilisateurs</label>
                  <input type="number" min="0" placeholder="Illimité"
                    value={form.limites.utilisateurs || ''}
                    onChange={e => setLim('utilisateurs', e.target.value)}/>
                </div>
                <div className="pm-form-group">
                  <label><Package size={12}/> Produits</label>
                  <input type="number" min="0" placeholder="Illimité"
                    value={form.limites.produits || ''}
                    onChange={e => setLim('produits', e.target.value)}/>
                </div>
              </div>

              <div className="pm-modal-footer">
                <button type="button" className="pm-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="pm-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="pm-spin"/> Enregistrement…</>
                    : <><Check size={13}/> {editing ? 'Enregistrer' : 'Créer le plan'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
